import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { Loader2, Save, Mail, Users, Shield, Briefcase } from 'lucide-react';

const ROLE_CONFIGS = [
  {
    key: 'staff',
    label: 'Staff',
    icon: Users,
    description: 'Frontline care workers, support staff',
    color: 'bg-blue-100 text-blue-700',
  },
  {
    key: 'manager',
    label: 'Manager',
    icon: Briefcase,
    description: 'Team leads, service managers',
    color: 'bg-purple-100 text-purple-700',
  },
  {
    key: 'admin',
    label: 'Admin',
    icon: Shield,
    description: 'Organisation administrators',
    color: 'bg-slate-100 text-slate-700',
  },
];

const OnboardingSettings = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [courses, setCourses] = useState([]);
  const [settings, setSettings] = useState({
    welcome_email_enabled: true,
    staff_course_ids: [],
    manager_course_ids: [],
    admin_course_ids: [],
    staff_course_titles: [],
    manager_course_titles: [],
    admin_course_titles: [],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, coursesRes] = await Promise.all([
        api.get('/settings/onboarding'),
        api.get('/builder/courses'),
      ]);
      setSettings(settingsRes.data);
      setCourses(coursesRes.data || []);
    } catch (error) {
      toast.error('Failed to load onboarding settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/settings/onboarding', settings);
      toast.success('Onboarding settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleCourse = (roleKey, courseId) => {
    const fieldKey = `${roleKey}_course_ids`;
    setSettings(prev => {
      const current = prev[fieldKey] || [];
      const updated = current.includes(courseId)
        ? current.filter(id => id !== courseId)
        : [...current, courseId];
      return { ...prev, [fieldKey]: updated };
    });
  };

  if (loading) {
    return (
      <DashboardLayout requiredRoles={['super_admin']}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout requiredRoles={['super_admin']}>
      <div className="max-w-4xl mx-auto p-6" data-testid="onboarding-settings">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#2a3e5d]">Team Onboarding</h1>
            <p className="text-slate-600 mt-1">
              Configure welcome emails and recommended courses for new team members
            </p>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#20acf6] hover:bg-[#3bb8f7]"
            data-testid="save-onboarding-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Settings
          </Button>
        </div>

        {/* Welcome Email Toggle */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#f8fafc] flex items-center justify-center">
                  <Mail className="h-6 w-6 text-[#20acf6]" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Welcome Email</h3>
                  <p className="text-sm text-slate-500">
                    Automatically send a personalised welcome email when team members accept their invitation
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.welcome_email_enabled !== false}
                onCheckedChange={(v) => setSettings(prev => ({ ...prev, welcome_email_enabled: v }))}
                data-testid="welcome-email-toggle"
              />
            </div>
          </CardContent>
        </Card>

        {/* Role-based Course Recommendations */}
        <h2 className="text-lg font-semibold text-[#2a3e5d] mb-4">Recommended Courses by Role</h2>
        <p className="text-sm text-slate-500 mb-6">
          Select which courses are recommended for each team member role. These appear in their welcome email and dashboard.
        </p>

        <div className="space-y-6">
          {ROLE_CONFIGS.map((role) => {
            const Icon = role.icon;
            const selectedIds = settings[`${role.key}_course_ids`] || [];
            const fallbackTitles = settings[`${role.key}_course_titles`] || [];

            return (
              <Card key={role.key} data-testid={`role-card-${role.key}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${role.color}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{role.label}</CardTitle>
                      <CardDescription>{role.description}</CardDescription>
                    </div>
                    <Badge variant="outline" className="ml-auto">
                      {selectedIds.length} selected
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {fallbackTitles.length > 0 && selectedIds.length === 0 && (
                    <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                      Default titles: {fallbackTitles.join(', ')}. Select specific courses below to override.
                    </div>
                  )}
                  <ScrollArea className="max-h-64">
                    <div className="space-y-2">
                      {courses.length > 0 ? courses.map((course) => (
                        <div
                          key={course.id}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors"
                        >
                          <Checkbox
                            id={`${role.key}-${course.id}`}
                            checked={selectedIds.includes(course.id)}
                            onCheckedChange={() => toggleCourse(role.key, course.id)}
                          />
                          <label
                            htmlFor={`${role.key}-${course.id}`}
                            className="flex-1 cursor-pointer"
                          >
                            <span className="text-sm font-medium text-slate-900">{course.title}</span>
                            {course.category && (
                              <Badge variant="outline" className="ml-2 text-xs">
                                {course.category}
                              </Badge>
                            )}
                          </label>
                        </div>
                      )) : (
                        <p className="text-sm text-slate-400 italic py-4 text-center">
                          No courses created yet. Create courses in the Course Builder first.
                        </p>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default OnboardingSettings;
